<script setup lang="ts">
/**
 * MarkdownWidgetDisplay — Renders markdown text with inline widget tokens.
 *
 * Replaces raw `v-html` rendering. Handles:
 * 1. Pre-extracting widget tokens from text (replacing with placeholders)
 * 2. Parsing markdown via marked.js
 * 3. Splitting the HTML by placeholders
 * 4. Rendering HTML segments via v-html and widget segments via Vue components
 *
 * @example
 * <MarkdownWidgetDisplay
 *   :content="'Hello @[John](user:123), call {{contact:user:123}}'"
 *   class="text-sm"
 * />
 */
import { marked } from 'marked'
import { computed, defineAsyncComponent, type Component } from 'vue'

import { ensureBuiltinsRegistered } from './registerBuiltins'
import { sanitizeMarkdownHtml } from './sanitizeMarkdownHtml'
import { preExtractTokens, splitByPlaceholders } from './tokenParser'
import type { ParsedToken, RenderSegment } from './types'
import { getWidgetComponent, type ComponentEntry } from './widgetRegistry'

// Ensure built-in widgets are always registered
ensureBuiltinsRegistered()

// Configure marked
marked.use({ breaks: true, gfm: true })

const props = defineProps<{
  content?: string | null
  class?: string
}>()

// ── Resolve component from registry entry ─────────────────────────

function resolveComponent(entry: ComponentEntry): Component {
  if (typeof entry === 'function') {
    return defineAsyncComponent(entry as () => Promise<{ default: Component }>)
  }
  return (entry as { default: Component }).default
}

// ── Compute renderable segments ───────────────────────────────────

const segments = computed<RenderSegment[]>(() => {
  if (!props.content) return []

  // Step 1: Pre-extract tokens, replace with placeholders
  const { cleaned, tokens } = preExtractTokens(props.content)

  // Step 2: Parse markdown; sanitize the rendered HTML via DOM walk to drop
  // anything the editor could not have produced (allowlist in
  // `sanitizeMarkdownHtml`). Color spans get their Tailwind class injected
  // here so no CSS rules tied to `[data-color]` selectors are needed.
  const parsed = marked.parse(cleaned, { gfm: true, breaks: true }) as string
  const html = sanitizeMarkdownHtml(parsed)

  // Step 3: Split HTML by placeholders
  if (tokens.size === 0) {
    return [{ type: 'html', html }]
  }

  const parts = splitByPlaceholders(html, tokens)

  // Step 4: Map to render segments
  return parts.map((part) => {
    if (part.type === 'html') {
      return { type: 'html' as const, html: part.html }
    }

    // Resolve widget component from registry
    const widgetType = part.token.type === 'mention' ? 'mention' : (part.token as ParsedToken & { widgetType: string }).widgetType
    const entry = getWidgetComponent(widgetType)

    if (!entry) {
      // Fallback: render raw token text
      return { type: 'html' as const, html: part.token.raw }
    }

    return {
      type: 'widget' as const,
      token: part.token,
      component: resolveComponent(entry),
    }
  })
})
</script>

<template>
  <div
    :class="[
      'prose prose-sm dark:prose-invert max-w-none min-w-0 max-w-full break-words [overflow-wrap:anywhere]',
      $props.class,
    ]"
  >
    <template v-for="(seg, idx) in segments" :key="idx">
      <!-- HTML segment: rendered via v-html -->
      <span v-if="seg.type === 'html'" v-html="seg.html" />
      <!-- Widget segment: rendered via dynamic component -->
      <component
        v-else
        :is="(seg as RenderSegment & { component: unknown }).component"
        :token="(seg as RenderSegment & { token: ParsedToken }).token"
        class="inline"
      />
    </template>
  </div>
</template>
